import Vue from "vue";
import { DurationUnit } from "~/enums/durationUnit";
import { CommunicationsPanelAvailabilityEnd } from "~/enums/forms/communicationsPanelAvailabilityEnd";
import { CommunicationsPanelAvailabilityStart } from "~/enums/forms/communicationsPanelAvailabilityStart";
import { ModelType } from "~/enums/modelType";
import { ReferenceNodeType } from '~/enums/referenceNodeType';
import { StringHelper } from "~/helpers/stringHelper";
import { GenericType, IGenericTypeable } from '~/interfaces/genericTypeInterfaces';
import { IImposter, IImposterFactory, IImposterOptions } from '~/interfaces/imposterInterfaces';
import { IModelTypeable, ITrackedModel } from '~/interfaces/modelInterfaces';
import { ITitle } from '~/interfaces/propertyInterfaces';
import { IReferenceNodeable } from '~/interfaces/referencePathInterfaces';
import { ComparisonType } from '~/models/conditionalRules/comparisonType';
import { JoinType } from "~/models/conditionalRules/joinType";
import IConditionalRuleReferencedControl from '~/models/conditionalRules/referencedControl';
import { TargetType } from '~/models/conditionalRules/targetType';
import CustomGeneratedIdSettings from "~/models/customGeneratedIdSettings";
import { DatetimeMode } from '~/models/datetimeMode';
import FormElementDynamicFieldsHandler from "~/models/dynamicFields/handlers/formElementDynamicFieldsHandler";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import Imposter from '~/models/imposter';
import ObjectModel from "~/models/objectModel";
import ObjectModelLimit from "~/models/objectModelLimit";
import ElementHandler from "~/services/elementHandler";
import { BuilderType } from "../../../common/enums/builderType";
import { DocumentOwnerType } from "../../../common/enums/documentOwnerType";
import { VersionType } from "../../../common/enums/versionType";
import { ObjectHelper } from "../../../common/helpers/objectHelper";
import { AttributeMetadataInterface } from "../../../common/interfaces/attributeMetadataInterface";
import IRootBuilderModel from "../../../common/interfaces/builderModelInterfaces";
import { ICapable } from "../../../common/interfaces/capableInterfaces";
import { INavigableValidatableModel } from "../../../common/interfaces/navigableValidationInterfaces";
import IRepo from "../../../common/interfaces/repo";
import Capable from "../../../common/mixins/capable";
import Shareable from "../../../common/mixins/shareable";
import Validatable from "../../../common/mixins/validatable";
import FormBuilderValidation from "../../../common/models/navigableValidations/formBuilderValidation";
import Organization from "../../../common/models/organization";
import { RelationType } from "../../../common/models/relationType";
import Share from "../../../common/models/share";
import FormBuilderModel from "../../../common/models/tracked/formBuilderModel";
import { IPeekerOptions } from "../../../common/services/peeker";
import Repo from "../../../common/state/modules/repo";
import store from "../../../common/state/store";
import { FormDetailStepModelType } from "../enums/formDetailStepModelType";
import FormProvider from "../providers/formProvider";
import Selected from "../store/modules/selected";
import FormElementConditionalRuleSet from "./conditionalRules/formElementConditionalRuleSet";
import CustomStatus from "./customStatus";
import FormDetailAgency from "./formDetails/formDetailAgency";
import FormDetailInstruction from "./formDetails/formDetailInstruction";
import FormDetailObjectModelPrerequisite from "./formDetails/formDetailObjectModelPrerequisite";
import FormDetailProcess from "./formDetails/formDetailProcess";
import FormDetailReminder from "./formDetails/formDetailReminder";
import FormDetailResource from "./formDetails/formDetailResource";
import FormDetailSeo from "./formDetails/formDetailSeo";
import FormDetailStep from "./formDetails/formDetailStep";
import FormDetailStepFactory from "./formDetails/formDetailStepFactory";
import ElementContainer from "./formElements/elementContainer";
import FormElement, { IFormElement } from "./formElements/formElement";
import { Capability } from "./formElements/formElementCapability";
import FormElementCapabilityManager from './formElements/formElementCapabilityManager';
import { FormElementType } from "~/enums/formElementType";
import PageBreak from "./formElements/pageBreak";
import AddressValidation from "./formElements/templates/addressValidation";
import ElementsTemplate from "./formElements/templates/elementsTemplate";
import MMCCProviderValidation from "./formElements/templates/mmccProviderValidation";
import PanValidation from "./formElements/templates/panValidation";
import ThirdPartyCompleter from "./formElements/templates/thirdPartyCompleter";
import FormSection from "./formSections/formSection";
import ApprovalStep from "./workflow/approvalStep";
import PaymentStep from "./workflow/paymentStep";
import WorkflowStep from "./workflow/workflowStep";
import WorkflowStepFactory from "./workflow/workflowStepFactory";
import { WorkflowType } from "~/enums/forms/workflowSteps/workflowType";
import {DocumentType} from "~/enums/documentType";
import DateTimeFormat = Intl.DateTimeFormat;
import lockstepDebounce from "~/lockstepDebounce";
import {ConditionalRuleBuiltInValueType, FormSubmissionConditionalRuleBuiltInValueTypes, FormSubmissionStepConditionalRulePaymentStepBuiltInValueTypes} from "~/models/conditionalRules/conditionalRuleBuiltInValueType";
import IAdvancedFilterable from "~/interfaces/advancedFilterInterfaces";
import {Hash} from "~/interfaces/hashInterfaces";
import {FormApplicationStatusName, FormApplicationStatusType} from "~/enums/forms/workflowSteps/formApplicationStatusType";

export interface IForm extends ITrackedModel, ITitle, ICapable, IGenericTypeable, IReferenceNodeable, IModelTypeable, IImposterFactory, IConditionalRuleReferencedControl, IAdvancedFilterable {
  titleOrDefault: string
  elementContainerElements: IFormElement[]
  formSections: FormSection[]
  workflowStepsArray: WorkflowStep[]
  workflowStepsWithFormSections: ApprovalStep[]
  paymentSteps: WorkflowStep[]
  getFormElements: IFormElement[]
}

export default class Form extends Shareable(Capable(Validatable(FormBuilderModel))) implements IRootBuilderModel, INavigableValidatableModel<Form>, IForm {

  form = this;
  organization: Organization;

  attributes_metadata: {[key: string]: AttributeMetadataInterface};

  except = ['form', 'workflow_steps', 'justApproved', 'createEmbeddedObject', 'organization', 'shares', 'custom_statuses', 'instructions', 'processes', 'resources', 'seo', 'agency', 'reminders', 'custom_submission_id_settings'];

  workflow_steps: { [key: string]: WorkflowStep } = {};
  custom_statuses: {[key: string]: CustomStatus } = {};
  instructions: {[key: string]: FormDetailInstruction } = {};
  processes: {[key: string]: FormDetailProcess } = {};
  resources: {[key: string]: FormDetailResource } = {};
  agency: FormDetailAgency;
  seo: FormDetailSeo;
  // it'd be nice to rename this to custom_generated_id_settings so it's the same as License,
  // but then that'd need a migration =/
  custom_submission_id_settings: CustomGeneratedIdSettings;
  theme_type: number;
  reminders: {[key: string]: FormDetailReminder} = {};

  title: string;
  u_at: string;
  c_at: string;
  justApproved: boolean;
  attempted_to_publish: boolean;

  form_owner_type: DocumentOwnerType;
  has_form_profile_page: boolean;
  enable_field_tracking: boolean;
  application_due_type: number;
  min_completion_time: number;
  min_completion_time_unit: number;
  max_completion_time: number;
  max_completion_time_unit: number;
  min_approval_time: number;
  min_approval_time_unit: number;
  max_approval_time: number;
  max_approval_time_unit: number;
  short_description: string;
  additional_description: string;
  include_time: boolean;
  due_custom_description: string;
  due_specified_date: DateTimeFormat;
  due_specified_window_open_date: DateTimeFormat;
  due_specified_window_close_date: DateTimeFormat;
  due_window_open_date_month: number;
  due_window_open_date_day: number;
  due_window_open_date_time_of_day: number;
  due_window_open_date_time_am_or_pm: number;
  due_window_close_date_month: number;
  due_window_close_date_day: number;
  due_window_close_date_time_of_day: number;
  due_window_close_date_time_am_or_pm: number;
  draft_expiration_action: number;
  draft_expiration_new_application_status: string;
  cost_has_range: boolean;
  min_cost: string;
  max_cost: string;
  fixed_cost: string;
  apply_custom_text_enabled: boolean;
  apply_section_header: string;
  apply_sub_text: string;
  apply_button_label: string;
  external_pdf_url: string;
  indexed_element_ids: string[];
  form_linked_object_model_ids: Array<string>;
  form_linked_object_models_is_enabled: boolean;
  linked_object_model_is_restricted: boolean;
  linked_object_model_limit_client_id: any;
  object_model_prerequisites_join_type: JoinType;
  object_model_prerequisites: { [key: string]: FormDetailObjectModelPrerequisite } = {};
  communications_panel_enabled: boolean;
  communications_panel_availability_start: CommunicationsPanelAvailabilityStart;
  communications_panel_availability_end: CommunicationsPanelAvailabilityEnd;
  communications_panel_group_id: string;
  communications_panel_group: {name: string, id: string};
  communications_panel_sla_amount: number;
  communications_panel_sla_unit: DurationUnit;
  communications_panel_sla_user_ids: string[];
  communications_panel_sla_users: Array<{first_name: string, last_name: string, id: string}>;
  communications_panel_message_placeholder: string;
  _all_statuses: string[] = [];
  seo_keywords: string[];
  application_id?: string;

  static repo: IRepo<Form, FormProvider> = new Repo<Form, FormProvider>('forms', Form, {}, new FormProvider(Form, 'form', 'forms'));
  repo: IRepo<this, FormProvider<this>>;

  _objectModelPrerequisiteHandler: ElementHandler = new ElementHandler(this, FormDetailObjectModelPrerequisite, 'object_model_prerequisites');
  get objectModelPrerequisiteHandler(): ElementHandler {
    return this._objectModelPrerequisiteHandler;
  }

  //******************** VALIDATABLE ********************//
  _validation: FormBuilderValidation = new FormBuilderValidation(this);
  //******************** VALIDATABLE END ********************//

  static get displayName(): string {
    return 'Form';
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      '_snapshots',
      'snapshots',
      'is_published',
      'u_at',
      'attributes_metadata',
      'request'
    ];
  }

  get TRACKED_ARRAYS(): string[] {
    return [
      'form_linked_object_model_ids'
    ];
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'workflow_steps': { type: WorkflowStep, relationType: RelationType.dictionary },
      'instructions': { type: FormDetailStep, relationType: RelationType.dictionary },
      'processes': { type: FormDetailStep, relationType: RelationType.dictionary },
      'resources': { type: FormDetailResource, relationType: RelationType.dictionary },
      'seo': { type: FormDetailSeo, relationType: RelationType.singleObject },
      'agency': { type: FormDetailAgency, relationType: RelationType.singleObject },
      'reminders': { type: FormDetailReminder, relationType: RelationType.dictionary},
      'shares': {type: Share, relationType: RelationType.collection},
      'custom_statuses': {type: CustomStatus, relationType: RelationType.dictionary},
      'custom_submission_id_settings': { type: CustomGeneratedIdSettings, relationType: RelationType.singleObject },
      'object_model_prerequisites': { type: FormDetailObjectModelPrerequisite, relationType: RelationType.dictionary },
    };
  }

  createEmbeddedObject = {
    workflow_steps: (parent, json): WorkflowStep => {
      return WorkflowStepFactory.create(parent, json['workflow_type']);
    },
    instructions: (parent, json): FormDetailInstruction => {
      return FormDetailStepFactory.create(parent, FormDetailStepModelType.instruction);
    },
    processes: (parent, json): FormDetailProcess => {
      return FormDetailStepFactory.create(parent, FormDetailStepModelType.process);
    },
    resources: (parent, json): FormDetailResource => {
      return new FormDetailResource(parent, json['sort_order']);
    },
    seo: (parent, json): FormDetailSeo => {
      return new FormDetailSeo(parent);
    },
    agency: (parent, json): FormDetailAgency => {
      return new FormDetailAgency(parent);
    },
    reminders: (parent, json): FormDetailReminder => {
      return new FormDetailReminder(parent, json['sort_order']);
    },
    custom_statuses: (parent, json): CustomStatus => {
      return new CustomStatus().loadFromJson(json, parent);
    },
    custom_submission_id_settings: (parent, json): CustomGeneratedIdSettings => {
      return new CustomGeneratedIdSettings(parent);
    },
    object_model_prerequisites: (parent, json): FormDetailObjectModelPrerequisite => {
      return new FormDetailObjectModelPrerequisite();
    },
  };

  initializeEmbedded<T extends FormDetailObjectModelPrerequisite>(parent: Form, item: T, position: number, json: object = {}) {
    this.objectModelPrerequisiteHandler.updateElementPositions(parent, item, position);
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
    item.addToRepo();

    Vue.set(parent[item.rootName], item.client_id, item);
    this.saveChangesToStore();

    return item;
  }

  get rootName() {
    return 'form';
  }

  get collectionKey() {
    return 'forms';
  }

  get classKey(): string {
    return "form";
  }

  get docType(): DocumentType {
    return DocumentType.form;
  }

  get storeName(): string {
    return `${this.rootName}Store`;
  }

  get workflowStepsArray(): WorkflowStep[] {
    return ObjectHelper.toArray(this.workflow_steps);
  }

  get workflowStepsWithFormSections(): ApprovalStep[] {
    return (<ApprovalStep[]>this.workflowStepsArray)
      .filter(ws => ws.form_section);
  }

  get formSections(): FormSection[] {
    return this.workflowStepsWithFormSections.map(ws => ws.form_section);
  }

  get hasMultipleFormSections(): boolean {
    return this.formSections.length > 1;
  }

  appendElement(elementType: FormElementType, position: number, elementJson: object = {}): FormElement {
    const formSection = this.currentFormSection();
    if (position === null || position === undefined) {
      position = formSection.nextChildSortOrder();
    }
    return this.createElementForParent(formSection, elementType, position, elementJson);
  }

  createResource(sortOrder): FormDetailResource {
    const resource = new FormDetailResource(this, sortOrder);
    this.placeTrackAndSave(resource);
    return resource;
  }

  createProcessStep(): FormDetailProcess {
    return this.createFormDetailStep(FormDetailStepModelType.process);
  }
  createInstructionStep(): FormDetailInstruction {
    return this.createFormDetailStep(FormDetailStepModelType.instruction);
  }

  createFormDetailStep(type): FormDetailStep {
    const step = FormDetailStepFactory.create(this, type);
    this.placeTrackAndSave(step);
    return step;
  }

  createFormDetailReminder() {
    const reminder = new FormDetailReminder(this);
    this.placeTrackAndSave(reminder);
    return reminder;
  }

  private placeTrackAndSave(child) {
    this.placeInDictionary(child);
    this.trackCreationDeletion(child.client_id, null, child.asJson(), child);
    this.saveChangesToStore();
  }

  createWorkflowStep(workflowStepType): WorkflowStep {
    const ws = WorkflowStepFactory.create(this, workflowStepType);
    return this.attachWorkflowStep(ws);
  }

  attachWorkflowStep(ws: WorkflowStep): WorkflowStep {
    ws.afterAppend();
    WorkflowStep.repo.add(ws);
    Vue.set(this.workflow_steps, ws.client_id, ws);

    this.trackCreationDeletion(ws.client_id, null, ws.asJson(), ws);

    this.saveChangesToStore();
    return ws;
  }

  appendWorkflow(workflowStepType: WorkflowType): WorkflowStep {
    return this.createWorkflowStep(workflowStepType);
  }

  createFormElement(elementType): FormElement {
    let el: FormElement;
    switch (elementType) {
      case FormElementType.pageBreak:
        el = new PageBreak();
        break;
      case FormElementType.thirdParty:
        el = new ThirdPartyCompleter();
        break;
      case FormElementType.USPSAddress:
        el = new AddressValidation();
        break;
      case FormElementType.template:
        el = new ElementsTemplate();
        break;
      case FormElementType.panValidation:
        el = new PanValidation();
        break;
      case FormElementType.MMCCProviderValidation:
        el = new MMCCProviderValidation();
        break;
      default:
        el = new FormElement();
        break;
    }
    return el;
  }

  createElementForParent(parent: ElementContainer, elementType: FormElementType, position: number, elementJson: object = {}): FormElement {
    const el = this.createFormElement(elementType).initialize(this.rootModel, parent, elementType, elementJson);
    el.sort_order = position;
    el.initializeAfterParentSet();
    parent.trackCreationDeletion(el.client_id, null, el.asJson(), el);
    if (parent.element_type !== FormElementType.grid) {
      parent.updateElementPositions(el, position);
    }
    FormElement.repo.add(el);
    Selected.formElement.set(el.selectableFormElement);
    Vue.set(parent.form_elements, el.client_id, el);

    this.saveChangesToStore();

    return el;
  }

  findElementContainer(id: string): ElementContainer {
    return FormElement.repo.find(id) || FormSection.repo.find(id);
  }

  createElementForParentId(parentId, elementType, position, elementJson: object = {}) {
    const parent = this.findElementContainer(parentId);
    this.createElementForParent(parent, elementType, position, elementJson);
  }

  moveElementToParent(elementId, parentId, newPosition) {
    const element = FormElement.repo.find(elementId);
    const newParent = this.findElementContainer(parentId);
    const oldParent = element.parent;

    if (newParent === oldParent) {
      element.sort_order = newPosition;  // same parent
    } else {
      oldParent.trackCreationDeletion(element.client_id, element.asJson(), null, element);

      element.sort_order = newPosition;
      newParent.trackCreationDeletion(element.client_id, null, element.asJson(), element);
    }

    if (newParent.element_type !== FormElementType.grid) {
      newParent.updateElementPositions(element, newPosition);
    }

    if (newParent !== oldParent) {
      Vue.delete(oldParent.form_elements, element.client_id);
      element.parent = newParent;
      Vue.set(newParent.form_elements, element.client_id, element);
    }

    element.afterMove(oldParent, newParent);

    this.saveChangesToStore();
  }

  moveElementToNewCell(elementId, parentId, newPosition) {
    const element = FormElement.repo.find(elementId);
    if (parentId === element.parent.client_id) {
      element.sort_order = newPosition;
    } else {
      this.moveElementToParent(elementId, parentId, newPosition);
    }

    this.saveChangesToStore();
  }

  swapElementPositions(elementId1, elementId2) {
    const element1 = FormElement.repo.find(elementId1);
    const element2 = FormElement.repo.find(elementId2);
    [element1.sort_order, element2.sort_order] = [element2.sort_order, element1.sort_order];
    this.saveChangesToStore();
  }

  currentFormSection(): FormSection {
    return Selected.formSection.get();
  }

  updateWorkflowStepPositions(movedElement, newParent) {
    movedElement.defaultAction.jump_to.to_client_id = newParent.jump_to_client_id;
    newParent.jump_to_client_id = movedElement.client_id;
  }

  get customStatusesArray(): CustomStatus[] {
    return Object.values(this.custom_statuses);
  }

  get vSelectOptionsFromStatuses() {
    const options = [];
    Object.keys(this.custom_statuses).forEach((k) => {
      options.push(this.custom_statuses[k].vOption)
    });
    let sortedOptions = options.sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
    return sortedOptions;
  }

  getConditionalRuleSetDependenciesFor(controlId: string): FormElementConditionalRuleSet[] {
    return <FormElementConditionalRuleSet[]>FormElementConditionalRuleSet.repo.all().filter(s => {
      return s.conditionalRulesArray.some(r => r.matchesDependenciesFor(controlId));
    });
  }

  get getFormElements(): FormElement[] {
    const elements = [];
    this.formSections.forEach(fs => {
      elements.push(...fs.formElementsArrayDeep);
    });
    return elements;
  }

  get getFormElementsForPdf(): FormElement[] {
    let excludedElementTypes = [...FormElementCapabilityManager.LAYOUT_CONTAINER_CONTROLS, FormElementType.checkbox, FormElementType.radioButton, FormElementType.dropdownItem, FormElementType.heading, FormElementType.subheading, FormElementType.paragraph, FormElementType.pageBreak, FormElementType.divider]
    return this.getFormElements.filter(formElement => !excludedElementTypes.includes(formElement.element_type))
  }

  referenceableFormElementsFor(element: FormElement | null, {excludeSingleLayoutElements}: { excludeSingleLayoutElements?: boolean } = {}): FormElement[] {
    excludeSingleLayoutElements = excludeSingleLayoutElements ?? true // keeps original functionality by default
    return this.getFormElements.filter(triggerElement => {
      // must not be current element, must be referenceable
      if ((element !== null && triggerElement.client_id === element.client_id) || !triggerElement.can(Capability.referenceable)) {
        return false;
      }

      // exclude "grid" (i.e. element containers) that are not repeatable (which is original functionality)
      if (excludeSingleLayoutElements && triggerElement.can(Capability.layoutContainer) && !triggerElement.is_repeatable) {
        return false
      }

      // if in repeatable
      if (triggerElement.repeatableAncestors[0] && element && element.repeatableAncestors) {
        return element.repeatableAncestors.includes(triggerElement.repeatableAncestors[0]);
      }

      return true;
    });
  }

  createObjectModelPrerequisite(): void {
    const newObjectModelPrerequisite = this.createEmbeddedObject.object_model_prerequisites(this, {});
    newObjectModelPrerequisite.initialize(this, this.nextSortOrderFor(newObjectModelPrerequisite.rootName));
    this.initializeEmbedded(this, newObjectModelPrerequisite, this.nextSortOrderFor(newObjectModelPrerequisite.rootName));
  }

  get objectModelPrerequisitesArray(): FormDetailObjectModelPrerequisite[] {
    return ObjectHelper.toArray(this.object_model_prerequisites)
      .sort((a, b) => a.sort_order - b.sort_order);
  }

  getElementsOfType(...formElementTypes: FormElementType[]): FormElement[] {
    return this.getFormElements.filter(el => {
      return formElementTypes.includes(el.element_type)
    });
  }

  get elementContainerElements(): FormElement[] {
    return this.getElementsOfType(...FormElementCapabilityManager.LAYOUT_CONTAINER_CONTROLS)
  }

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return this.attributes_metadata[propName] ? this.attributes_metadata[propName].display_name : StringHelper.titleize(propName);
    }
  }

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled Form";
    }
  }

  get displayTitle(): string {
    return this.title || 'Untitled Form'
  }

  get initialWorkflowStep(): WorkflowStep {
    let workflowSteps = Object.keys(this.workflow_steps)
      .map(key => this.workflow_steps[key])
      .sort((a, b) => a.sort_order - b.sort_order);
    return workflowSteps[0]
  }

  get paymentSteps(): PaymentStep[] {
    return this.workflowStepsArray.filter(step => step.isPaymentStep) as PaymentStep[]
  }

  get allStatuses(): string[] {
    return this._all_statuses.map(status => {
      let result = status.replace( /([A-Z])/g, "$1" )
      let finalResult = result.charAt(0).toUpperCase() + result.slice(1);
      return finalResult.trim();
    })
  }

  get sortedStatuses(): CustomStatus[] {
    return this.sortProperty('custom_statuses');
  }

  get sortedInstructions(): FormDetailInstruction[] {
    return this.sortProperty('instructions');
  }

  get sortedProcesses(): FormDetailProcess[] {
    return this.sortProperty('processes');
  }

  get sortedResources(): FormDetailResource[] {
    return this.sortProperty('resources');
  }

  // Snake case for compatibility with other ruleSet root models.
  get join_type(): JoinType {
    return this.object_model_prerequisites_join_type;
  }
  set join_type(val: JoinType) {
    this.object_model_prerequisites_join_type = val;
  }

  createStep(stepType): FormDetailStep {
    const step = FormDetailStepFactory.create(this, stepType);
    this.placeInDictionary(step);
    this.trackCreationDeletion(step.client_id, null, step.asJson(), step);
    this.saveChangesToStore();
    return step;
  }

  // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(this.lastPeekerOptions || {area: this.currentBuilderArea(), modelId: this.clientIdOrId});
      if (this.attempted_to_publish) {
        lockstepDebounce(this.validate, 250);
      }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.builderStoreName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = {area: this.currentBuilderArea(), modelId: this.clientIdOrId}): void {
    store.commit(`${this.builderStoreName}/saveChanges`, {changes, peekerOptions});
  }

  // ******************** TRACK CHANGES END ******************** //


  // ******************** INDEXABLE ******************** //
  get indexedElements(): FormElement[] {
    return this.indexed_element_ids.map(id => FormElement.repo.find(id)).filter(form => form);
  }

  isElementIndexed(elementOrId: FormElement | string): boolean {
    if (typeof elementOrId === 'string') {
      return this.indexed_element_ids.includes(elementOrId);
    } else {
      return this.indexed_element_ids.includes(elementOrId.client_id);
    }
  }

  removeElementIndex(element: FormElement): void {
    const index = this.indexed_element_ids.indexOf(element.client_id);
    if (index !== -1) {
      this.indexed_element_ids = this.indexed_element_ids.filter(el => {
        return el !== element.client_id
      })
    }
  }
  // ******************** INDEXABLE END******************** //

  get formLinkedObjectModel(): ObjectModel {
    return ObjectModel.repo.findBy((om) => om.id === this.form_linked_object_model_ids[0])
  }

  set formLinkedObjectModel(val) {
    let om = ObjectModel.repo.find(val.client_id);
    if (!om) {
      ObjectModel.repo.addFromJson(val)
    }
  }

  get linkedObjectModelLimit(): ObjectModelLimit {
    if (this.formLinkedObjectModel.objectModelLimitsArray) {
      return this.formLinkedObjectModel.objectModelLimitsArray.find(objectModelLimit => objectModelLimit.client_id === this.linked_object_model_limit_client_id)
    }
  }

  // ******************** Form/FormTemplate Common ******************** //
  get isForm(): boolean {
    return true;
  }

  get isTemplate(): boolean {
    return false;
  }

  get builderStoreName(): string {
    return this.storeName;
  }

  get builderType(): BuilderType {
    return BuilderType.form;
  }

  get versionType(): VersionType {
    return VersionType.form;
  }

  get versionTypeName(): string {
    return 'form';
  }

  get collectionRoot(): string {
    return 'forms';
  }
  // ******************** Form/FormTemplate Common END ******************** //

  get isPublished(): boolean {
    return this.is_published && Date.now() > Date.parse(this.u_at);
  }

  get modelType(): ModelType {
    return ModelType.Form
  }

  get model_type(): ModelType {
    return this.modelType;
  }

  get customGeneratedIdSettingsDynamicFieldsHandler() {
    return new FormElementDynamicFieldsHandler({initialStepOnly: true, form: this});
  }

  get recurringWindowOpenDate() {
    const year = new Date().getFullYear()
    let hour = 0
    if (this.due_window_open_date_time_of_day && this.due_window_open_date_time_am_or_pm) {
      // Convert 12 to 0 to ease handling of AM / PM
      const propertyFormatHour = this.due_window_open_date_time_of_day === 12 ? 0 : this.due_window_open_date_time_of_day
      // AM = 0, PM = 1
      hour = this.due_window_open_date_time_am_or_pm === 0 ? propertyFormatHour : propertyFormatHour + 12
    }
    const date = new Date(year, this.due_window_open_date_month, this.due_window_open_date_day, hour)
    return date
  }

  get recurringWindowCloseDate() {
    const year = new Date().getFullYear()
    let hour = 0
    if (this.due_window_close_date_time_of_day && this.due_window_close_date_time_am_or_pm) {
      // Convert 12 to 0 to ease handling of AM / PM
      const propertyFormatHour = this.due_window_close_date_time_of_day === 12 ? 0 : this.due_window_open_date_time_of_day
      // AM = 0, PM = 1
      hour = this.due_window_close_date_time_am_or_pm === 0 ? propertyFormatHour : propertyFormatHour + 12
    }
    const date = new Date(year, this.due_window_close_date_month, this.due_window_close_date_day, hour)
    return date
  }

  /**
   * this is a convenience method so that areas of the application that intake both a form or
   * form element can execute this method without error
   * @see FormElementDynamicFieldsHandler#formElementSections
   */
  get repeatableAncestors(): any[] {
    return []
  }

  /**
   * Find a form element actually linked to this form - do not go to the repo as it
   * may find a form element related to a published form instead of this base form
   */
  findElement(id: string): FormElement {
    let found: FormElement
    for (const workflowStep of this.workflowStepsWithFormSections) {
      found = workflowStep.findElement(id)
      if (found) break
    }
    return found
  }

  get genericType(): GenericType {
    return ModelType.Object
  }

  get referenceNodeType(): ReferenceNodeType {
    return ReferenceNodeType.Form
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter({
      ...ObjectHelper.sliceProperties(this as IForm, 'id', 'genericType', 'modelType'),
      isCollection: false,
    }, options)
  }


  // ******************** IConditionalRuleReferencedControl ******************** //
  isValidForConditionalLogic: boolean = true;
  get referenceNameOrDefault(): string {
    return this.title;
  }

  displayConditionType(targetType: TargetType, resetReferenceControl: boolean = false): ComparisonType {
    return ComparisonType.record
  }

  getChoiceComparisonOptions(): any[] {
    return [];
  }

  getConditionDateTimeMode(): DatetimeMode {
    return null;
  }

  getConditionMaxDateTime(): string {
    return "";
  }

  getConditionMinDateTime(): string {
    return "";
  }

  targetTypes(): TargetType[] {
    return [];
  }

  // ******************** IConditionalRuleReferencedControl END******************** //

  getReferencableControls(): FormElement[] {
    // find indexed elements from this form rather than repo to ensure accurate data; reducing to avoid multiple iterations
    return this.formSections.reduce<FormElement[]>((indexedElements, formSection) => {
      formSection.forEachFormElement((formElement) => {
        if (formElement.isIndexed) indexedElements.push(formElement);
      }, true);

      return indexedElements;
    }, []);
  }

  getReferencedControl(id: string): FormElement {
    return this.getReferencableControls().find(formElement => formElement.client_id === id);
  }

  friendlyReferencableControlsName = 'field';

  get builtInValueSectionHeading(): string {
    return 'About submission';
  }

  conditionalRuleBuiltInValueTypes(): ConditionalRuleBuiltInValueType[] {
    return FormSubmissionConditionalRuleBuiltInValueTypes;
  }
  conditionalRulePaymentStepBuiltInValueTypes(): ConditionalRuleBuiltInValueType[] {
    return FormSubmissionStepConditionalRulePaymentStepBuiltInValueTypes;
  }

  builtInValueChoiceComparisonOptions(builtInValueType: ConditionalRuleBuiltInValueType): Hash[] {
    switch (builtInValueType) {
      case ConditionalRuleBuiltInValueType.FormSubmissionApplicationStatus:
        const options: Hash[] = this.customStatusesArray.map(status => ({label_title: status.name, client_id: status.client_id}));

        // form_submission.rb #current_application_status currently defaults to Completed default status even if it does not exist in :custom_statuses
        const completedStatusName: string = FormApplicationStatusName[FormApplicationStatusType.completed];
        if (!options.find(o => o.label_title === completedStatusName)) {
          options.push({label_title: completedStatusName, client_id: String(FormApplicationStatusType.completed)});
        }

        return options;
      case ConditionalRuleBuiltInValueType.FormSubmissionStepPaymentMethod:
        return [
          {label_title: "Check", client_id: "check"},
          {label_title: "Online Payment", client_id: "online payment"}
        ]
      default:
        return [];
    }
  }

  conditionalRuleBuiltInValueReferenceName(type: ConditionalRuleBuiltInValueType, referenceId: string): string {
    if (!this.conditionalRuleBuiltInValueTypes().includes(type) && !this.conditionalRulePaymentStepBuiltInValueTypes().includes(type)) {
      throw new Error(`conditionalRuleBuiltInValueTypes() does not include type ${type}`)
    }

    switch (type) {
      case ConditionalRuleBuiltInValueType.FormSubmissionStepSubmittedOnDate:
      case ConditionalRuleBuiltInValueType.FormSubmissionStepPaymentMethod:
        return this.workflowStepsArray.find(ws => ws.client_id === referenceId).titleOrDefault;
      default:
        throw new Error(`Not implemented for type: ${type}`);
    }
  }
}
